import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  EventEmitter,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { Subject, Subscription } from 'rxjs';
import { first, takeUntil } from 'rxjs/operators';
import { extend, get, merge } from 'lodash';

import { LicensesPageService, NotificationService } from '@nrwl/common-lib';
import { NavigationProvider } from '@nrwl/navigation-abstract';

import { IWizardEntry } from '../../wizard.entry';
import { WizardComponent } from '../../wizard.component';
import { ActiveTestWizardService } from '../../services/active.test/active.test.wizard.service';
import { ActiveTestTypesComponent } from './active.test.types.page/active.test.types.component';
import { ActiveTestLicenseButtonComponent } from './active.test.license.button/active.test.license.button.component';

@Component({
  selector: 'active-test-wizard',
  providers: [ActiveTestWizardService],
  templateUrl: '../../wizard.template.html',
  styleUrls: ['../../wizard.component.style.scss', '../../wizard.button.style.scss']
})
export class ActiveTestWizardComponent
  extends WizardComponent
  implements OnInit, AfterViewInit, OnDestroy, IWizardEntry
{
  @ViewChild('contentPlaceholder', { read: ViewContainerRef, static: true })
  contentRef: ViewContainerRef;
  @ViewChild('subContentPlaceholder', { read: ViewContainerRef, static: true })
  subContentRef: ViewContainerRef;
  @ViewChild('buttonPlaceholder', { read: ViewContainerRef, static: true })
  buttonRef: ViewContainerRef;

  wizardConfig: any; //eslint-disable-line @typescript-eslint/no-explicit-any
  wizardId: string;
  dataId: any; //eslint-disable-line @typescript-eslint/no-explicit-any
  onClose = new EventEmitter<any>(); //eslint-disable-line @typescript-eslint/no-explicit-any
  widgetConfig: any; //eslint-disable-line @typescript-eslint/no-explicit-any
  state: Router;
  wizardService: ActiveTestWizardService;
  openWizard: any; //eslint-disable-line @typescript-eslint/no-explicit-any
  subscription: Subscription;
  changeDetectorRef: ChangeDetectorRef;
  closeWizardObj: Subscription;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  licenses: any = {
    max: 0,
    inUse: 0
  };

  private readonly unsubscribe: Subject<void> = new Subject();

  constructor(
    state: Router,
    wizardService: ActiveTestWizardService,
    changeDetectorRef: ChangeDetectorRef,
    private readonly activatedRoute: ActivatedRoute,
    private readonly factoryResolver: ComponentFactoryResolver,
    private readonly notificationService: NotificationService,
    private readonly licenseService: LicensesPageService,
    private readonly navigator: NavigationProvider
  ) {
    super();
    this.state = state;
    this.wizardService = wizardService;
    this.changeDetectorRef = changeDetectorRef;
  }

  ngOnInit() {
    this.activatedRoute.queryParams.pipe(first()).subscribe(data => {
      const defaultParams = merge({}, this.dataId, JSON.parse(get(data, 'defaultParams', '{}')));

      this.wizardService.output = {};

      if (this.wizardId === 'active_test_update') {
        this.wizardService.editMode = true;
      }
      this.wizardService.openWizard(
        this.wizardId,
        this.wizardConfig,
        defaultParams,
        () => this.onSuccessCallback({}),
        () => this.onFailureCallback()
      );
    });

    this.wizardService.registerToggleListener(this.notifyOnToggleSubContent.bind(this));
    this.hasActiveTestLicense();
  }

  ngAfterViewInit() {
    this.createContentComponent();
    this.createButtonComponent();
    this.changeDetectorRef.detectChanges();
  }

  ngOnDestroy() {
    if (this.closeWizardObj) {
      this.closeWizardObj.unsubscribe();
    }
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  get(object: any, path: string): any {
    return get(object, path);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  onSuccessCallback(object: any): void {
    this.notificationService.onMonitorCreationSuccess(extend({}, object));
  }

  onFailureCallback(): void {
    const activeTest = this.wizardService.output;
    this.notificationService.onMonitorCreationFailure(activeTest);
  }

  createButtonComponent(): void {
    const factory = this.getFactoryResolver().resolveComponentFactory(
      ActiveTestLicenseButtonComponent
    );
    this.buttonRef.clear();
    const component = this.buttonRef.createComponent(factory);
    const casted = component.instance as ActiveTestLicenseButtonComponent;
    casted.wizardCtrl = this;
    casted.step = this.wizardService.currentStep;
  }

  // eslint-disable-next-line @typescript-eslint/ban-types
  toggleClose(callBack?: Function): void {
    if (!this.wizardService.editMode) {
      this.wizardService.toggleClose(() => {
        this.onClose.emit(true);
        if (callBack) {
          callBack();
        }
      });
    } else {
      this.wizardService.toggleClose(() => this.onEditCloseCallback());
    }
  }

  onEditCloseCallback(): void {
    this.activatedRoute.queryParams.subscribe(data => {
      if (data) {
        const defaultParams = JSON.parse(get(data, 'defaultParams', '{}'));
        const monitorId = get(defaultParams, 'id');
        this.navigator.navigateToWizard(null, {
          queryParams: { monitorId: monitorId },
          relativeTo: this.activatedRoute.parent
        });
      }
    });
  }

  toggleReOpen(): void {
    this.wizardService.reOpen();
  }

  notifyOnToggleSubContent(onOff: boolean): void {
    if (onOff) {
      this.createSubContentComponent();
    } else {
      this.subContentRef.clear();
    }
  }

  getWizardService(): ActiveTestWizardService {
    return this.wizardService;
  }

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  getFactoryResolver(): any {
    return this.factoryResolver;
  }

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  getContentViewRef(): any {
    return this.contentRef;
  }

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  getSubContentViewRef(): any {
    return this.subContentRef;
  }

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  resolveComponentType(pageId: string): any {
    switch (pageId) {
      case 'activetest.types.html':
        return ActiveTestTypesComponent;
      default: {
        console.error('Unrecognized template name:', pageId);
        return null;
      }
    }
  }

  hasActiveTestLicense(): void {
    this.licenseService
      .getLicenseCount()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        value => {
          if (value && value.length) {
            const monitorLicense = value.find(element => element.featureName === 'ACT-NUM-TESTS');
            if (monitorLicense) {
              this.licenses.max = monitorLicense.count || 0;
              this.licenses.inUse = monitorLicense.used || 0;
            }
          }
        },
        () => {}
      );
  }

  isValidStep(step: string): boolean {
    return this.wizardConfig.steps.find(i => i.id === step);
  }
}
